import i18next from "src/i18n/config";
import RouteService from "src/services/routeService";
import { invalidateOnSuccess, transformErrorResponse } from "src/store/helpers";
import { ApiResponse, BasicApiResponse, ListResponse } from "src/types";

import { AdminBuilder } from "./tags";

const jsonHeaders = { "content-type": "application/json" };

type GetAdminGroupsParams = {
    ids?: number[];
    userEmail?: string;
    userId?: string;
    includeParents?: boolean;
};

type MoveGroupLicensesParams = {
    sourceId: number;
    targetGroupId: number;
};

type RevokeGroupLicensesParams = {
    id: number;
    reason?: string;
};

type DeleteAdminGroupParams = {
    id: number;
    force: boolean;
};

const groups = (builder: AdminBuilder) => ({
    getAdminGroups: builder.query<API.Group[], GetAdminGroupsParams>({
        providesTags: ["AdminGroups"],
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.technicalDifficulties.message")),
        transformResponse: (response: ListResponse<API.Group>) => response.list,
        query: async (params) => {
            const base = await RouteService.getAdminGroupsRoute();
            return { url: base, params };
        },
    }),

    moveGroupLicenses: builder.mutation<API.Group, MoveGroupLicensesParams>({
        invalidatesTags: invalidateOnSuccess("AdminGroups"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("pages:adminGroupsPage.merge.toast.error")),
        transformResponse: (response: ApiResponse<API.Group>) => response.model,
        query: async ({ sourceId, targetGroupId }) => {
            const base = await RouteService.getAdminGroupsRoute();
            return {
                url: `${base}${sourceId}/licenses`,
                method: "PATCH",
                data: { targetGroupId },
                headers: jsonHeaders,
            };
        },
    }),

    revokeGroupLicenses: builder.mutation<BasicApiResponse, RevokeGroupLicensesParams>({
        invalidatesTags: invalidateOnSuccess("AdminGroups", "LicenseStatistics"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("pages:adminGroupsPage.revoke.toast.error")),
        query: async ({ id, reason }) => {
            const base = await RouteService.getAdminGroupsRoute();
            const trimmedReason = reason?.trim();
            return {
                url: `${base}${id}/licenses`,
                method: "DELETE",
                data: trimmedReason ? { reason: trimmedReason } : {},
                headers: jsonHeaders,
            };
        },
    }),

    deleteAdminGroup: builder.mutation<void, DeleteAdminGroupParams>({
        invalidatesTags: invalidateOnSuccess("AdminGroups", "LicenseStatistics"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("modals:adminDeleteGroup.toast.error")),
        query: async ({ id, force }) => {
            const base = await RouteService.getAdminGroupsRoute();
            return { url: `${base}${id}`, method: "DELETE", data: { force }, headers: jsonHeaders };
        },
    }),
});

export default groups;
